import React, { useMemo } from 'react';
import { useFormContext, get, useController } from 'react-hook-form';
import { TableProvider } from '@panwds/react-table';
import { Banner } from '@panwds/react-ui';

/**
 * A TableProvider connected to a form.
 * Enables selecting rows and connects the selected rows to the form.
 *
 * Default values for the table must be passed directly.
 */
function TableFieldProvider({ children, name, register, data, columns, defaultValue, rowSelectMode, ...rest }) {
    var _a;
    const context = useFormContext();
    /* istanbul ignore next */
    if (process.env.NODE_ENV === "development" && context === null) {
        throw new Error("Attempted to render TableFieldProvider outside of a Form context. Make sure your component is rendered inside <Form>.");
    }
    const { formState } = context;
    const errorMessage = (_a = get(formState.errors, name)) === null || _a === void 0 ? void 0 : _a.message;
    const { field: { onChange, value: values }, } = useController({
        name,
        defaultValue: (defaultValue !== null && defaultValue !== void 0 ? defaultValue : rowSelectMode === "single") ? undefined : [],
        rules: register,
    });
    const selectedRowIds = useMemo(() => {
        if (rowSelectMode === "single") {
            return { [values]: true };
        }
        else {
            return (values !== null && values !== void 0 ? values : []).reduce((acc, key) => {
                acc[key] = true;
                return acc;
            }, {});
        }
    }, [values]);
    return (React.createElement(React.Fragment, null,
        React.createElement(TableProvider, { ...rest, data: data, columns: columns, enableRowSelect: true, rowSelectMode: rowSelectMode, onRowSelect: (row, checked) => {
                const rowIds = new Set(values);
                function toggleRow(rowId) {
                    if (checked) {
                        rowIds.add(rowId);
                    }
                    else {
                        rowIds.delete(rowId);
                    }
                }
                if (row.subRows.length) {
                    row.subRows.forEach((row) => toggleRow(row.id));
                }
                else {
                    toggleRow(row.id);
                }
                if (rowSelectMode === "single") {
                    onChange(row.id);
                }
                else {
                    onChange(Array.from(rowIds));
                }
            }, onAllRowSelect: (checked) => {
                const valueSet = new Set(values);
                if (checked) {
                    data.forEach((datum, index) => valueSet.add(rest.getRowId(datum, index)));
                    onChange(Array.from(valueSet));
                }
                else {
                    data.forEach((datum, index) => valueSet.delete(rest.getRowId(datum, index)));
                    onChange(Array.from(valueSet));
                }
            }, useControlledState: (state) => {
                return React.useMemo(() => ({
                    ...state,
                    selectedRowIds: selectedRowIds,
                }), [state, selectedRowIds]);
            } }, children),
        errorMessage && (React.createElement("div", { className: "tw-mt-2" },
            React.createElement(Banner, { appearance: "error", showIcon: true }, errorMessage)))));
}

export { TableFieldProvider };
//# sourceMappingURL=TableFieldProvider.esm.js.map
